// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * CheatCodeHandler - Global keyboard listener for cheat codes
 * 
 * Listens for configured key combinations and triggers cheat effects.
 * Key combos are defined in GameConfig.cheats
 */

import { useMemo } from 'react';
import { useFuel } from '../contexts/FuelContext';
import { useCredits } from '../contexts/CreditsContext';
import { useShipHealth } from '../contexts/ShipHealthContext';
import GameConfig from '../config/GameConfig';
import useGlobalHotkey from '../hooks/useGlobalHotkey';

const CheatCodeHandler = ({ onCheatActivated }) => {
  const { addFuel } = useFuel();
  const { addCredits } = useCredits();
  const { repairAll } = useShipHealth();

  const bindings = useMemo(() => {
    const map = {};

    if (GameConfig.cheats.fuel?.enabled) {
      map[GameConfig.cheats.fuel.keyCombo] = () => {
        const amount = GameConfig.cheats.fuel.amount;
        addFuel(amount);
        onCheatActivated?.({ type: 'fuel', amount, message: `⚡ CHEAT: +${amount}% FUEL` });
      };
    }

    if (GameConfig.cheats.credits?.enabled) {
      map[GameConfig.cheats.credits.keyCombo] = () => {
        const amount = GameConfig.cheats.credits.amount;
        addCredits(amount, 'cheat code');
        onCheatActivated?.({ type: 'credits', amount, message: `⚡ CHEAT: +${amount} CREDITS` });
      };
    }

    if (GameConfig.cheats.repair?.enabled) {
      map[GameConfig.cheats.repair.keyCombo] = () => {
        repairAll();
        onCheatActivated?.({ type: 'repair', message: '⚡ CHEAT: ALL SYSTEMS REPAIRED' });
      };
    }

    return map;
  }, [addFuel, addCredits, repairAll, onCheatActivated]);

  useGlobalHotkey(bindings, { enabled: GameConfig.cheats.enabled });

  return null;
};

export default CheatCodeHandler;
